import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import SectorDetailHero from "../../components/pages/Sectors/SectorDetailHero/SectorDetailHero";
import BoardsDetailExploreSlider from "../../components/pages/AllProducts/Boards/BoardsDetailExploreSlider/BoardsDetailExploreSlider";
import Hospitality from "../../components/pages/Home/Hospitality/Hospitality";
import AboutStartNewProject from "../../components/pages/About/AboutStartNewProject/AboutStartNewProject";
import "./SectorDetail.css";
import { getSectorDetailBySlug } from "../../data/sectorDetailData";
import { getPublicSectorBySlug, getPublicSectors } from "../../api/sectors";
import { getPageContent } from "../../api/content";
import { resolveMediaUrl } from "../../utils/media";
import { getAboutStartNewProjectContent } from "../../utils/aboutStartNewProjectSection";
import iconWalls from "../../assets/images/sectors/s1.svg";
import iconCeiling from "../../assets/images/sectors/s2.svg";
import iconExterior from "../../assets/images/sectors/s3.svg";
import leftArrowIcon from "../../assets/icons/left.svg";
import rightArrowIcon from "../../assets/icons/right.svg";

const fallbackSectorTabs = [
  { slug: "commercial", label: "Commercial systems" },
  { slug: "residential", label: "Residential" },
  { slug: "hospitality", label: "Hospitality" },
  { slug: "healthcare", label: "Healthcare" },
  { slug: "education", label: "Education" },
  { slug: "entertainment", label: "Entertainment" },
];

const SectorDetail = () => {
  const { i18n } = useTranslation();
  const { sectorSlug } = useParams();
  const fallbackSector = getSectorDetailBySlug(sectorSlug);
  const [apiSector, setApiSector] = useState(null);
  const [apiTabs, setApiTabs] = useState([]);
  const [aboutContent, setAboutContent] = useState({});

  useEffect(() => {
    let isMounted = true;
    const loadAbout = async () => {
      try {
        const data = await getPageContent({
          page: "about",
          language: String(i18n.language || "en").toLowerCase(),
        });
        if (isMounted) setAboutContent(data && typeof data === "object" ? data : {});
      } catch {
        if (isMounted) setAboutContent({});
      }
    };
    loadAbout();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const [sectorData, listData] = await Promise.all([
          getPublicSectorBySlug(sectorSlug, language),
          getPublicSectors(language),
        ]);
        if (!isMounted) return;
        setApiSector(sectorData || null);
        setApiTabs(Array.isArray(listData) ? listData : []);
      } catch {
        if (isMounted) {
          setApiSector(null);
          setApiTabs([]);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [sectorSlug, i18n.language]);

  const sector = useMemo(() => {
    if (!apiSector) return fallbackSector;
    const merged = { ...(fallbackSector || {}), ...apiSector };
    merged.benefits = {
      interiorWalls:
        apiSector?.benefits?.interiorWalls ||
        fallbackSector?.benefits?.interiorWalls ||
        [],
      ceilingSystems:
        apiSector?.benefits?.ceilingSystems ||
        fallbackSector?.benefits?.ceilingSystems ||
        [],
      exteriorCladding:
        apiSector?.benefits?.exteriorCladding ||
        fallbackSector?.benefits?.exteriorCladding ||
        [],
    };
    merged.project = {
      ...(fallbackSector?.project || {}),
      ...(apiSector?.project || {}),
    };
    merged.image =
      resolveMediaUrl(apiSector?.image || apiSector?.imageUrl || apiSector?.mediaUrl) ||
      apiSector?.image ||
      fallbackSector?.image;
    merged.project.image =
      resolveMediaUrl(apiSector?.project?.image || apiSector?.project?.imageUrl) ||
      apiSector?.project?.image ||
      fallbackSector?.project?.image;
    return merged;
  }, [apiSector, fallbackSector]);

  const sectorTabs = useMemo(() => {
    if (!Array.isArray(apiTabs) || !apiTabs.length) return fallbackSectorTabs;
    return apiTabs
      .map((item) => ({
        slug: item?.slug || "",
        label: item?.title || item?.slug || "",
      }))
      .filter((item) => item.slug)
      .map((item) => ({
        ...item,
        label: item.label === item.slug ? item.label : item.label,
      }));
  }, [apiTabs]);

  if (!sector) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="sectorDetailPage">
      <Navbar theme="light" />
      <SectorDetailHero sector={sector} />
      <div className="sectorDetailPage-content-guide">
        <section className="sectorDetailTabs" id="sector-detail-content">
          <div className="sectorDetailTabs-inner">
            {sectorTabs.map((tab) => (
              <Link
                key={tab.slug}
                to={`/sectors/${tab.slug}`}
                className={`sectorDetailTab ${sector.slug === tab.slug ? "is-active" : ""}`}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </section>

        <section className="sectorDetailAbout">
          <div className="sectorDetailSection-inner">
            <p className="sectorDetailMeta">{sector.subtitle}</p>
            <h2>About this sector</h2>
            <p className="sectorDetailAboutText">{sector.about}</p>
          </div>
        </section>

        <section className="sectorBenefits">
          <div className="sectorDetailSection-inner">
            <h2 className="sectorBenefitsTitle">
              Benefits of IRQGYP <span>solutions</span>
            </h2>
          </div>

          <div className="sectorBenefitsGrid">
            <article className="sectorBenefitCard">
              <img src={iconWalls} alt="" className="sectorBenefitIcon" />
              <div>
                <h3>Interior Walls</h3>
                <ul>
                  {sector.benefits.interiorWalls.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </article>

            <article className="sectorBenefitCard">
              <img src={iconCeiling} alt="" className="sectorBenefitIcon" />
              <div>
                <h3>Ceiling Systems</h3>
                <ul>
                  {sector.benefits.ceilingSystems.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </article>

            <article className="sectorBenefitCard">
              <img src={iconExterior} alt="" className="sectorBenefitIcon" />
              <div>
                <h3>Exterior Cladding</h3>
                <ul>
                  {sector.benefits.exteriorCladding.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </section>

        {/* <section className="sectorProject">
          <div className="sectorProject-top">
            <h2>
              {sector.project?.title || "Project title here - "}
              <span>Display some of your projects</span>
            </h2>
            <div className="sectorProject-summary">
              <p>{sector.project.description}</p>
              <Link to="/allprojects" className="sectorProject-moreLink">
                More Details <span>&rarr;</span>
              </Link>
              <div className="sectorProject-controls" aria-hidden>
                <button type="button">
                  <img src={leftArrowIcon} alt="" />
                </button>
                <button type="button">
                  <img src={rightArrowIcon} alt="" />
                </button>
              </div>
            </div>
          </div>

          <div className="sectorProject-imageWrap">
            <img src={sector.project.image} alt={sector.title} />
          </div>
        </section> */}

        <BoardsDetailExploreSlider />
        <Hospitality />
        <AboutStartNewProject content={getAboutStartNewProjectContent(aboutContent)} />
      </div>
      <Footer />
    </div>
  );
};

export default SectorDetail;
